// pages/CreateQuotePage.js
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DatePicker, { registerLocale } from 'react-datepicker';
import { fr } from 'date-fns/locale';
import { isValid, differenceInCalendarDays } from 'date-fns';
import Select from 'react-select';

import 'react-datepicker/dist/react-datepicker.css';
import './CreateQuotePage.css';

/* ------------------------------------------------------------------ */
/* CONSTANTES                                                         */
/* ------------------------------------------------------------------ */
registerLocale('fr', fr);

const companyLogo = process.env.PUBLIC_URL + '/Logo_horizontal_fondnoir-_1_.webp';

const companyInfo = {
  name: 'Ouest Pro Event',
  address: '4 Rue de L\'argoat',
  zipCode: '22200',
  city: 'Saint-Agathon',
  phone: '06.76.98.99.89',
  email: 'oceane.legrand@ouest-proevent.fr / theo.cioclei@ouest-proevent.fr',
  siret: '123 456 789 00012',
  tvaRate: 20
};

/* ------------------------------------------------------------------ */
/* COMPOSANT                                                          */
/* ------------------------------------------------------------------ */
function CreateQuotePage() {
  /* ---------------------- ROUTER / REFS --------------------------- */
  const location = useLocation();
  const navigate = useNavigate();
  const clientInfo = location.state?.clientInfo;
  const quoteRef = useRef(null);

  /* ------------------------- ÉTAT GLOBAL -------------------------- */
  const [formData, setFormData] = useState({
    firstName: clientInfo?.first_name || '',
    lastName: clientInfo?.last_name || '',
    email: clientInfo?.email || '',
    phone: clientInfo?.phone || '',
    startDate: null,
    endDate: null,
    deliveryMethod: 'pickup',
    deliveryAddress: { street: '', zipCode: '', city: '' }
  });

  const [products,            setProducts]            = useState([]);
  const [selectedProducts,    setSelectedProducts]    = useState([]);
  const [eventFormulas,       setEventFormulas]       = useState([]);
  const [selectedServices,    setSelectedServices]    = useState([]);
  const [productsAvailability,setProductsAvailability]= useState({});
  const [loading,             setIsLoading]           = useState(true);

  const [washingOption, setWashingOption] = useState(false);
  const [washingPrice,  setWashingPrice]  = useState(20);

  const [deliveryDistance, setDeliveryDistance] = useState(0);
  const [deliveryPrice,    setDeliveryPrice]    = useState(0);

  const [paymentMethod, setPaymentMethod] = useState('online');

  const [quoteData,     setQuoteData]  = useState(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  /* -------------------------- HELPERS ----------------------------- */
  const calculateDays = () => {
    const { startDate, endDate } = formData;
    if (!isValid(startDate) || !isValid(endDate)) return 0;
    return differenceInCalendarDays(endDate, startDate) + 1; // +1 => inclut le dernier jour
  };

  const formatDate = (date) =>
    new Date(date).toLocaleDateString('fr-FR');

  /* ------------------------- REDIRECTIONS ------------------------- */
  useEffect(() => { if (!clientInfo) navigate('/clients'); },
            [clientInfo, navigate]);

  /* --------------------------- FETCH ------------------------------ */
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('https://backend.ouest-pro-event.fr/admin/products_admin');
        if (!res.ok) throw new Error('Erreur chargement produits');
        setProducts(await res.json());
      } catch (e) { console.error(e); }
      finally     { setIsLoading(false); }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('https://backend.ouest-pro-event.fr/event-formulas');
        if (!res.ok) throw new Error('Erreur chargement formules');
        const data = await res.json();
        setEventFormulas(data.formulas || []);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  /* ------------------- DISPONIBILITÉ PRODUITS --------------------- */
  const checkAllProductsAvailability = async () => {
    if (!isValid(formData.startDate) || !isValid(formData.endDate)) return;

    const availabilityData = {};
    for (const product of products) {
      try {
        const res = await fetch(`https://backend.ouest-pro-event.fr/availability/${product.id}`);
        if (!res.ok) continue;

        const { availability } = await res.json();
        let current = new Date(formData.startDate);
        const last  = new Date(formData.endDate);
        let ok = true, min = availability.stock;

        while (current <= last) {
          const key   = current.toISOString().split('T')[0];
          const avail = availability.stock - (availability.rented[key] || 0);
          if (avail < 1) ok = false;
          if (avail < min) min = avail;
          current.setDate(current.getDate() + 1);
        }
        availabilityData[product.id] = { isAvailable: ok, stockAvailable: min };
      } catch (e) { console.error(e); }
    }
    setProductsAvailability(availabilityData);
  };

  useEffect(() => { if (formData.startDate && formData.endDate) checkAllProductsAvailability(); },
            [formData.startDate, formData.endDate]); // eslint‑disable‑line

  /* -------------------------- HANDLERS ---------------------------- */
  /** Dates **/
  const handleStartDateChange = (date) =>
    setFormData(prev => ({ ...prev, startDate: date }));

  const handleEndDateChange = (date) =>
    setFormData(prev => ({ ...prev, endDate: date }));

  /** Champs texte / email / tel **/
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('delivery.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        deliveryAddress: { ...prev.deliveryAddress, [field]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  /** Livraison **/
  const handleDeliveryDistanceChange = (e) =>
    setDeliveryDistance(Number(e.target.value));

  const handleDeliveryPriceChange = (e) =>
    setDeliveryPrice(Number(e.target.value));

  /** Produits **/
  const handleAddProduct = (product) => {
    if (!product) return;
    setSelectedProducts(prev => {
      const found = prev.find(p => p.id === product.id);
      if (found)
        return prev.map(p => p.id === product.id
          ? { ...p, quantity: p.quantity + 1 }
          : p);
      return [...prev, {
        id: product.id,
        name: product.name,
        dailyPrice: product.dailyPrice,
        customDailyPrice: product.dailyPrice,
        quantity: 1,
        maxStock: product.stock
      }];
    });
  };

  const handleQuantityChange = (id, quantity) =>
    setSelectedProducts(prev =>
      prev.map(p =>
        p.id === id ? { ...p, quantity: Math.max(0, quantity) } : p));

  const handleCustomPriceChange = (id, price) =>
    setSelectedProducts(prev =>
      prev.map(p =>
        p.id === id ? { ...p, customDailyPrice: Math.max(0, Number(price)) } : p));

  const handleRemoveProduct = (id) =>
    setSelectedProducts(prev => prev.filter(p => p.id !== id));


    const createServiceEntry = (formula, item, index) => {
      const timestamp = Date.now();
      const baseKey = `${formula.id || 'formula'}-${item.id || index}-${timestamp}`;
      return {
        key: `service-${baseKey}-${Math.random().toString(36).slice(2, 8)}`,
        formulaId: formula.id || null,
        formulaName: formula.name || '',
        formulaItemId: item.id || null,
        title: item.title || formula.name || 'Prestation',
        details: item.details || '',
        quantity: 1,
        unitPrice: 0,
      };
    };
  
    const handleAddFormula = (formula) => {
      if (!formula) return;
      const items = Array.isArray(formula.items) && formula.items.length > 0
        ? formula.items
        : [{ id: null, title: formula.name, details: formula.description }];
  
      const newEntries = items.map((item, index) => createServiceEntry(formula, item, index));
      setSelectedServices(prev => [...prev, ...newEntries]);
    };
  
    const handleRemoveService = (key) => {
      setSelectedServices(prev => prev.filter(service => service.key !== key));
    };
  
    const handleServiceQuantityChange = (key, quantity) => {
      setSelectedServices(prev => prev.map(service => (
        service.key === key
          ? { ...service, quantity: Math.max(1, Number(quantity) || 1) }
          : service
      )));
    };
  
    const handleServicePriceChange = (key, price) => {
      setSelectedServices(prev => prev.map(service => (
        service.key === key
          ? { ...service, unitPrice: Math.max(0, Number(price) || 0) }
          : service
      )));
    };

  /* -------------------- QUOTE PREVIEW ----------------------------- */
  const generateQuotePreview = () => {
    if (!isValid(formData.startDate) || !isValid(formData.endDate)) {
      alert('Veuillez choisir des dates valides');
      return;
    }
    if (selectedProducts.length === 0) {
      alert('Veuillez sélectionner au moins un produit ou une prestation');
      return;
    }
    if (formData.deliveryMethod === 'delivery') {
      const { street, zipCode, city } = formData.deliveryAddress;
      if (!street || !zipCode || !city) {
        alert('Adresse de livraison incomplète');
        return;
      }
    }

    const days = calculateDays();
    // MODIFIÉ: suppression du calcul avec les jours
    // const subtotal = selectedProducts.reduce(
    //   (s, p) => s + p.quantity * p.customDailyPrice, 0);

    const productsSubtotal = selectedProducts.reduce(
      (s, p) => s + p.quantity * p.customDailyPrice, 0);
    const servicesSubtotal = selectedServices.reduce(
      (s, svc) => s + svc.quantity * svc.unitPrice, 0);
    const subtotal = productsSubtotal + servicesSubtotal;

    const washingCost = washingOption ? washingPrice : 0;
    const totalHT = subtotal + deliveryPrice + washingCost;
    const tvaAmount = totalHT * (companyInfo.tvaRate / 100);
    const totalTTC = totalHT + tvaAmount;

    const prepared = {
      quoteNumber:`D-TEMP-${Date.now().toString().slice(-6)}`,
      clientName:`${formData.firstName} ${formData.lastName}`,
      email: formData.email,
      phone: formData.phone,
      deliveryMethod: formData.deliveryMethod,
      deliveryAddress: formData.deliveryMethod==='delivery' ? formData.deliveryAddress : null,
      startDate: formData.startDate,
      endDate: formData.endDate,
      // items: selectedProducts.map(p => ({
      //   product_id: p.id,
      //   product_name: p.name,
      //   quantity: p.quantity,
      //   daily_price: p.customDailyPrice,
      //   subtotal: p.quantity * p.customDailyPrice  // MODIFIÉ: suppression du calcul avec les jours
      // })),
      items: [
        ...selectedProducts.map(p => ({
          type: 'product',
          product_id: p.id,
          product_name: p.name,
          quantity: p.quantity,
          daily_price: p.customDailyPrice,
          subtotal: p.quantity * p.customDailyPrice  // MODIFIÉ: suppression du calcul avec les jours
        })),
        ...selectedServices.map(svc => {
          const parts = [svc.title];
          if (svc.formulaName) parts.push(`(${svc.formulaName})`);
          if (svc.details) parts.push(`– ${svc.details}`);
          return {
            type: 'service',
            product_id: null,
            product_name: parts.join(' ').trim(),
            service_name: svc.title,
            formula_id: svc.formulaId,
            formula_name: svc.formulaName,
            formula_item_id: svc.formulaItemId,
            quantity: svc.quantity,
            daily_price: svc.unitPrice,
            subtotal: svc.quantity * svc.unitPrice,
          };
        }),
      ],
      days,
      subtotal,
      servicesSubtotal,
      deliveryPrice,
      deliveryDistance,
      washingCost,
      tvaAmount,
      totalPrice: totalTTC,
      paymentMethod
    };

    setQuoteData(prepared);
    setIsPreviewMode(true);
  };

  /* -------------------- QUOTE CREATION ---------------------------- */
  const handleCreateQuote = async () => {
    try {
      const yyyyMmDd = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

      const productItemsPayload = (quoteData.items || []).filter(item => item.type !== 'service').map(i => ({
        product_id: i.product_id,
        quantity:   i.quantity,
        daily_price:i.daily_price
      }));

      const serviceItemsPayload = (quoteData.items || []).filter(item => item.type === 'service').map(i => ({
        formula_item_id: i.formula_item_id,
        name: i.service_name || i.product_name,
        quantity: i.quantity,
        unit_price: i.daily_price
      }));

      const rentalData = {
        account_id: clientInfo.id,
        first_name: formData.firstName,
        last_name:  formData.lastName,
        email:      formData.email,
        phone:      formData.phone,
        startDate:  yyyyMmDd(formData.startDate),
        endDate:    yyyyMmDd(formData.endDate),
        delivery_method: formData.deliveryMethod,
        payment_method: quoteData.paymentMethod,
        washing_option: washingOption,
        washing_price:  washingPrice,
        delivery_address: formData.deliveryMethod==='delivery' ? formData.deliveryAddress : undefined,
        // items: quoteData.items.map(i => ({
        //   product_id: i.product_id,
        //   quantity:   i.quantity,
        //   daily_price:i.daily_price
        // }))
        items: productItemsPayload,
        services: serviceItemsPayload
      };

      const res = await fetch('https://backend.ouest-pro-event.fr/rentals', {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify(rentalData)
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Erreur création devis');
      const { rental_id } = await res.json();

      if (formData.deliveryMethod==='delivery' && (deliveryDistance>0 || deliveryPrice>0)) {
        await fetch(`https://backend.ouest-pro-event.fr/admin/rentals/${rental_id}/update_quote`, {
          method:'PUT', headers:{'Content-Type':'application/json'},
          body: JSON.stringify({
            // items: quoteData.items,
            items: quoteData.items.filter(item => item.type !== 'service'),
            total_price: quoteData.totalPrice,
            delivery_distance: deliveryDistance,
            delivery_price: deliveryPrice,
            washing_option: washingOption
          })
        });
      }
      alert(`Devis créé (ID : ${rental_id})`);
      navigate('/rentals');
    } catch (e) {
      alert(e.message);
    }
  };

  /* ---------------------------- RENDER ---------------------------- */

  /** Options pour React‑Select **/
  const productOptions = products.map(p => ({
    value: p.id, label: p.name, data: p
  }));

  const formulaOptions = eventFormulas.map(f => ({
    value: f.id,
    label: f.name,
    data: f,
  }));

  if (loading) return <div className="loading">Chargement des produits…</div>;

  return (
    <div className="create-quote-page">
      <h1>Créer un devis pour {formData.firstName} {formData.lastName}</h1>

      {/* ====================== FORMULAIRE ====================== */}
      {!isPreviewMode && (
        <div className="quote-form-container">
          {/* ---- INFOS CLIENT ---- */}
          <div className="form-section client-info-editable">
            <h2>Informations client</h2>
            <div className="client-details-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">Prénom</label>
                  <input id="firstName" name="firstName"
                         value={formData.firstName}
                         onChange={handleInputChange}/>
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">Nom</label>
                  <input id="lastName" name="lastName"
                         value={formData.lastName}
                         onChange={handleInputChange}/>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input type="email" id="email" name="email"
                         value={formData.email}
                         onChange={handleInputChange}/>
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Téléphone</label>
                  <input type="tel" id="phone" name="phone"
                         value={formData.phone}
                         onChange={handleInputChange}/>
                </div>
              </div>
            </div>
          </div>

          {/* ---- DATES ---- */}
          <div className="form-section">
            <h2>Dates de location</h2>
            <div className="separate-date-pickers">
              <div className="date-picker-container start-date">
                <label>Date de début</label>
                <DatePicker
                  selected={formData.startDate}
                  onChange={handleStartDateChange}
                  selectsStart
                  startDate={formData.startDate}
                  endDate={formData.endDate}
                  minDate={new Date()}
                  dateFormat="dd/MM/yyyy"
                  locale="fr"
                  popperPlacement="bottom-start"
                  className="date-input"
                  calendarClassName="custom-calendar"/>
              </div>
              <div className="date-picker-container end-date">
                <label>Date de fin</label>
                <DatePicker
                  selected={formData.endDate}
                  onChange={handleEndDateChange}
                  selectsEnd
                  startDate={formData.startDate}
                  endDate={formData.endDate}
                  minDate={formData.startDate || new Date()}
                  dateFormat="dd/MM/yyyy"
                  locale="fr"
                  popperPlacement="bottom-start"
                  className="date-input"
                  calendarClassName="custom-calendar"/>
              </div>
            </div>
            <div className="selected-dates-summary">
              <div className="date-info">
                <span className="date-label">Date de début :</span>
                <span className="date-value">
                  {formData.startDate ? formatDate(formData.startDate) : '---'}
                </span>
              </div>
              <div className="date-info">
                <span className="date-label">Date de fin :</span>
                <span className="date-value">
                  {formData.endDate ? formatDate(formData.endDate) : '---'}
                </span>
              </div>
              <div className="date-info highlight">
                <span className="date-label">Durée :</span>
                <span className="date-value">{calculateDays()} jour(s)</span>
              </div>
            </div>
          </div>

          {/* ---- LIVRAISON ---- */}
          <div className="form-section">
            <h2>Mode de livraison</h2>
            <div className="delivery-method-selector">
              <div className={`delivery-option ${formData.deliveryMethod==='pickup' ? 'selected':''}`}
                   onClick={() => setFormData(prev=>({...prev,deliveryMethod:'pickup'}))}>
                <div className="icon-container"><i className="fas fa-store"></i></div>
                <h3>Récupération sur place</h3>
              </div>
              <div className={`delivery-option ${formData.deliveryMethod==='delivery' ? 'selected':''}`}
                   onClick={() => setFormData(prev=>({...prev,deliveryMethod:'delivery'}))}>
                <div className="icon-container"><i className="fas fa-truck"></i></div>
                <h3>Livraison</h3>
              </div>
            </div>

            {formData.deliveryMethod==='delivery' && (
              <div className="delivery-address-form">
                <h3>Adresse de livraison</h3>
                <div className="form-group">
                  <label htmlFor="delivery.street">Rue *</label>
                  <input id="delivery.street" name="delivery.street"
                         value={formData.deliveryAddress.street}
                         onChange={handleInputChange}/>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="delivery.zipCode">Code postal *</label>
                    <input id="delivery.zipCode" name="delivery.zipCode"
                           value={formData.deliveryAddress.zipCode}
                           onChange={handleInputChange}/>
                  </div>
                  <div className="form-group">
                    <label htmlFor="delivery.city">Ville *</label>
                    <input id="delivery.city" name="delivery.city"
                           value={formData.deliveryAddress.city}
                           onChange={handleInputChange}/>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="deliveryDistance">Distance (km)</label>
                    <input type="number" id="deliveryDistance"
                           value={deliveryDistance}
                           onChange={handleDeliveryDistanceChange}/>
                  </div>
                  <div className="form-group">
                    <label htmlFor="deliveryPrice">Prix de livraison (€)</label>
                    <input type="number" id="deliveryPrice"
                           value={deliveryPrice}
                           onChange={handleDeliveryPriceChange}/>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ---- PRODUITS ---- */}
          <div className="form-section">
            <h2>Produits</h2>
            <div className="product-selection">
              <div className="product-dropdown-container">
                <Select
                  className="product-dropdown"
                  options={productOptions}
                  placeholder="Sélectionner un produit"
                  isClearable
                  formatOptionLabel={(opt) => {
                    const pid = opt.value;
                    const unavail = productsAvailability[pid] && !productsAvailability[pid].isAvailable;
                    return (
                      <div className={`product-option ${unavail?'unavailable-product':''}`}>
                        {opt.data.photos?.length
                          ? <img src={`https://backend.ouest-pro-event.fr/${opt.data.photos[0].url}`}
                                 alt={opt.label} className="product-option-image"/>
                          : <div className="product-option-placeholder"/>}
                        <div className="product-option-details">
                          <div className="product-option-name">
                            {opt.label}
                            {unavail && <span className="stock-warning"> (Non disponible)</span>}
                          </div>
                          <div className="product-option-price">
                            {opt.data.dailyPrice}€/jour – Stock :
                            {productsAvailability[pid]
                              ? productsAvailability[pid].stockAvailable
                              : opt.data.stock}
                          </div>
                        </div>
                      </div>
                    );
                  }}
                  onChange={(selected) => {
                    if (!selected) return;
                    const pid = selected.value;
                    const unavail = productsAvailability[pid] && !productsAvailability[pid].isAvailable;
                    if (unavail) { alert('Produit indisponible pour ces dates'); return; }
                    handleAddProduct(selected.data);
                  }}/>
              </div>

              {selectedProducts.length>0
                ? (
                  <table className="selected-products-table">
                    <thead>
                      <tr>
                        <th>Produit</th><th>Prix unitaire</th>
                        <th>Quantité</th><th>Total HT</th><th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedProducts.map(p => (
                        <tr key={p.id}>
                          <td>{p.name}</td>
                          <td className="text-right">
                            <input type="number" className="price-input"
                                   value={p.customDailyPrice}
                                   min="0" step="0.01"
                                   onChange={e=>handleCustomPriceChange(p.id,e.target.value)}/> €
                          </td>
                          <td className="quantity-cell">
                            <button className="quantity-btn"
                                    onClick={()=>handleQuantityChange(p.id,p.quantity-1)}>-</button>
                            <input type="number" min="1" max={p.maxStock}
                                   value={p.quantity}
                                   onChange={e=>handleQuantityChange(p.id,Number(e.target.value))}/>
                            <button className="quantity-btn"
                                    onClick={()=>handleQuantityChange(p.id,p.quantity+1)}>+</button>
                          </td>
                          <td className="text-right">
                            {/* MODIFIÉ: suppression du calcul avec les jours */}
                            {(p.quantity*p.customDailyPrice).toFixed(2)} €
                          </td>
                          <td><button className="remove-btn"
                                      onClick={()=>handleRemoveProduct(p.id)}>Supprimer</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
                : <div className="no-products-selected">Aucun produit sélectionné</div>}
                </div>
        </div>

        <div className="form-section">
          <h2>Formules évènement</h2>
          <div className="product-selection">
            <div className="product-dropdown-container">
              <Select
                className="product-dropdown"
                options={formulaOptions}
                placeholder="Ajouter une formule"
                isClearable
                formatOptionLabel={(opt) => (
                  <div className="formula-option">
                    <div className="formula-option-name">{opt.label}</div>
                    {opt.data.description && (
                      <div className="formula-option-description">{opt.data.description}</div>
                    )}
                    {opt.data.items && opt.data.items.length > 0 && (
                      <div className="formula-option-items">
                        {opt.data.items.map((item) => item.title).filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </div>
                )}
                onChange={(selected) => {
                  if (!selected) return;
                  handleAddFormula(selected.data);
                }}
              />
            </div>
            {selectedServices.length > 0 ? (
              <table className="selected-services-table">
                <thead>
                  <tr>
                    <th>Prestation</th>
                    <th>Formule</th>
                    <th>Prix</th>
                    <th>Quantité</th>
                    <th>Total HT</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedServices.map((service) => {
                    const total = service.quantity * service.unitPrice;
                    return (
                      <tr key={service.key}>
                        <td>
                          <div className="service-name-cell">
                            <span>{service.title}</span>
                            {service.details && <small>{service.details}</small>}
                          </div>
                        </td>
                        <td>{service.formulaName || '—'}</td>
                        <td className="text-right">
                          <input
                            type="number"
                            className="price-input"
                            value={service.unitPrice}
                            min="0"
                            step="0.01"
                            onChange={(e) => handleServicePriceChange(service.key, e.target.value)}
                          /> €
                        </td>
                        <td className="quantity-cell">
                          <button
                            className="quantity-btn"
                            onClick={() => handleServiceQuantityChange(service.key, service.quantity - 1)}
                          >
                            -
                          </button>
                          <input
                            type="number"
                            min="1"
                            value={service.quantity}
                            onChange={(e) => handleServiceQuantityChange(service.key, e.target.value)}
                          />
                          <button
                            className="quantity-btn"
                            onClick={() => handleServiceQuantityChange(service.key, service.quantity + 1)}
                          >
                            +
                          </button>
                        </td>
                        <td className="text-right">{total.toFixed(2)} €</td>
                        <td>
                          <button
                            className="remove-btn"
                            onClick={() => handleRemoveService(service.key)}
                          >
                            Retirer
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="no-products-selected">Aucune prestation ajoutée</div>
            )}
          </div>
          </div>

          {/* ---- OPTION LAVAGE ---- */}
          <div className="form-section">
            <h2>Options supplémentaires</h2>
            <div className="washing-option-container">
              <label>
                <input type="checkbox" checked={washingOption}
                       onChange={e=>setWashingOption(e.target.checked)}/>
                Option de lavage
              </label>
              {washingOption && (
                <div className="washing-price-input">
                  <label>Prix du lavage (€)</label>
                  <input type="number" min="0" step="0.01"
                         value={washingPrice}
                         onChange={e=>setWashingPrice(Number(e.target.value))}/>
                </div>
              )}
            </div>
          </div>

          {/* ---- PAIEMENT ---- */}
          <div className="form-section">
            <h2>Méthode de paiement</h2>
            <div className="payment-method-selector">
              <div className={`payment-option ${paymentMethod==='online'?'selected':''}`}
                   onClick={()=>setPaymentMethod('online')}>
                <div className="icon-container"><i className="fas fa-credit-card"/></div>
                <h3>Paiement en ligne</h3>
              </div>
              <div className={`payment-option ${paymentMethod==='transfer'?'selected':''}`}
                   onClick={()=>setPaymentMethod('transfer')}>
                <div className="icon-container"><i className="fas fa-university"/></div>
                <h3>Virement bancaire</h3>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button className="preview-btn" onClick={generateQuotePreview}>
              Aperçu du devis
            </button>
          </div>
        </div>
      )}

      {/* ====================== APERÇU ====================== */}
      {isPreviewMode && quoteData && (
        <div className="quote-preview-container">
          <div className="quote-actions">
            <button className="back-btn" onClick={()=>setIsPreviewMode(false)}>Retour</button>
            <button className="create-btn" onClick={handleCreateQuote}>Créer le devis</button>
          </div>

          <div ref={quoteRef} className="quote-preview">
            {/* --- En‑tête --- */}
            <div className="quote-header-section">
              <div className="quote-company-info">
                <img src={companyLogo} alt="Logo" className="company-logo"/>
                <div className="company-details">
                  <h3>{companyInfo.name}</h3>
                  <p>{companyInfo.address}</p>
                  <p>{companyInfo.zipCode} {companyInfo.city}</p>
                  <p>Tél : {companyInfo.phone}</p>
                  <p>Email : {companyInfo.email}</p>
                  <p>SIRET : {companyInfo.siret}</p>
                </div>
              </div>
              <div className="quote-reference">
                <h2>DEVIS</h2>
                <p><strong>N° :</strong> {quoteData.quoteNumber}</p>
                <p><strong>Date :</strong> {formatDate(new Date())}</p>
                <p><strong>Validité :</strong> 30 jours</p>
              </div>
            </div>

            {/* --- Client --- */}
            <div className="quote-client-info">
              <h3>Client</h3>
              <p><strong>Nom :</strong> {quoteData.clientName}</p>
              <p><strong>Email :</strong> {quoteData.email}</p>
              {quoteData.phone && <p><strong>Tél :</strong> {quoteData.phone}</p>}

              {quoteData.deliveryMethod==='delivery' && quoteData.deliveryAddress && (
                <>
                  <p><strong>Adresse de livraison :</strong></p>
                  <p>{quoteData.deliveryAddress.street}</p>
                  <p>{quoteData.deliveryAddress.zipCode} {quoteData.deliveryAddress.city}</p>
                  {quoteData.deliveryDistance>0 && (
                    <p><strong>Distance :</strong> {quoteData.deliveryDistance} km</p>
                  )}
                </>
              )}
            </div>

            {/* --- Détails devis --- */}
            <div className="quote-details">
              <h3>Détails du devis</h3>
              <p><strong>Période :</strong> {formatDate(quoteData.startDate)} au {formatDate(quoteData.endDate)}</p>
              <p><strong>Durée :</strong> {quoteData.days} jour{quoteData.days>1?'s':''}</p>

              <table className="quote-items-table">
                <thead>
                  <tr>
                    <th>Désignation</th><th>Qté</th><th>Prix unitaire</th>
                    <th>Total HT</th><th>TVA</th><th>Total TTC</th>
                  </tr>
                </thead>
                <tbody>
                  {quoteData.items.map(it => {
                    const tva = it.subtotal * companyInfo.tvaRate/100;
                    return (
                      <tr key={it.product_id}>
                        <td>{it.product_name}</td>
                        <td className="text-center">{it.quantity}</td>
                        <td className="text-right">{it.daily_price.toFixed(2)} €</td>
                        <td className="text-right">{it.subtotal.toFixed(2)} €</td>
                        <td className="text-right">{tva.toFixed(2)} €</td>
                        <td className="text-right">{(it.subtotal+tva).toFixed(2)} €</td>
                      </tr>
                    );
                  })}

                  {quoteData.deliveryPrice>0 && (
                    <tr className="delivery-row">
                      <td>Frais de livraison</td><td className="text-center">1</td>
                      <td className="text-right">{quoteData.deliveryPrice.toFixed(2)} €</td>
                      <td className="text-right">{quoteData.deliveryPrice.toFixed(2)} €</td>
                      <td className="text-right">{(quoteData.deliveryPrice*companyInfo.tvaRate/100).toFixed(2)} €</td>
                      <td className="text-right">{(quoteData.deliveryPrice*(1+companyInfo.tvaRate/100)).toFixed(2)} €</td>
                    </tr>
                  )}

                  {quoteData.washingCost>0 && (
                    <tr className="washing-row">
                      <td>Forfait lavage</td><td className="text-center">1</td>
                      <td className="text-right">{quoteData.washingCost.toFixed(2)} €</td>
                      <td className="text-right">{quoteData.washingCost.toFixed(2)} €</td>
                      <td className="text-right">{(quoteData.washingCost*companyInfo.tvaRate/100).toFixed(2)} €</td>
                      <td className="text-right">{(quoteData.washingCost*(1+companyInfo.tvaRate/100)).toFixed(2)} €</td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="5" className="text-right">Total HT</td>
                    <td className="text-right">
                      {(quoteData.subtotal+quoteData.deliveryPrice+quoteData.washingCost).toFixed(2)} €
                      </td>
                  </tr>
                  <tr>
                    <td colSpan="5" className="text-right">TVA ({companyInfo.tvaRate} %)</td>
                    <td className="text-right">{quoteData.tvaAmount.toFixed(2)} €</td>
                  </tr>
                  <tr className="total-row">
                    <td colSpan="5" className="text-right">Total TTC</td>
                    <td className="text-right">{quoteData.totalPrice.toFixed(2)} €</td>
                  </tr>
                  <tr className="deposit-row">
                    <td colSpan="5" className="text-right">Acompte (50 %)</td>
                    <td className="text-right">{(quoteData.totalPrice*0.5).toFixed(2)} €</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* --- Conditions --- */}
            <div className="quote-terms">
              <h3>Conditions</h3>
              <p>Devis valable 30 jours.</p>
              <p>Acompte de 50 % à la commande.</p>
              {paymentMethod==='transfer' && (
                <div className="bank-details">
                  <h4>Coordonnées bancaires</h4>
                  <p><strong>IBAN :</strong> FR76 1558 9228 0300 0744 9264 909</p>
                  <p><strong>BIC  :</strong> CMBRFR2BXXX</p>
                  <p><strong>Référence :</strong> Devis {quoteData.quoteNumber}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CreateQuotePage;